import { Calendar } from "primereact/calendar";
import { useRef } from "react";
import {
  Controller,
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDate } from "src/core/hooks/useDate";
import { useProfile } from "src/features/auth/hooks/useProfile";

export interface BirthDateFormData extends FieldValues {
  birthDate?: Date;
}

type BirthDateProps<T extends BirthDateFormData> = {
  form: UseFormReturn<T, any, T>;
  required: boolean;
};

export function BirthDateField<T extends BirthDateFormData>({
  form,
  required,
}: BirthDateProps<T>) {
  const calendarRef = useRef<Calendar>(null);

  const { t, i18n } = useTranslation();
  const { placeholderDate } = useDate();
  const { birthDateMin, birthDateMax } = useProfile();

  const error = form.formState.errors.birthDate as
    | Record<string, any>
    | undefined;

  return (
    <>
      <Controller
        name={"birthDate" as Path<T>}
        control={form.control}
        rules={{
          required: {
            value: required,
            message: t("Error.Required"),
          },
        }}
        render={({ field }) => (
          <Calendar
            inputId="birthDate"
            {...field}
            ref={calendarRef}
            invalid={!!error}
            showButtonBar={true}
            showIcon={true}
            locale={i18n.language}
            minDate={birthDateMin}
            maxDate={birthDateMax}
            placeholder={placeholderDate}
            todayButtonClassName="calendar-today-button"
            clearButtonClassName="calendar-clear-button"
            onShow={() => calendarRef.current?.focus()}
            onInput={(e) => {
              const raw = (e.target as HTMLInputElement).value;
              if (/^\d{8}$/.test(raw)) {
                let year, month, day;
                if (i18n.language.startsWith("hu")) {
                  year = parseInt(raw.slice(0, 4), 10);
                  month = parseInt(raw.slice(4, 6), 10) - 1;
                  day = parseInt(raw.slice(6, 8), 10);
                } else {
                  month = parseInt(raw.slice(0, 2), 10) - 1;
                  day = parseInt(raw.slice(2, 4), 10);
                  year = parseInt(raw.slice(4, 8), 10);
                }
                const date = new Date(year, month, day);
                if (!isNaN(date.getTime())) {
                  field.onChange(date);
                }
              }
            }}
          />
        )}
      />
      {error && <small className="p-error block">{error.message}</small>}
    </>
  );
}
